/**
 * Dashboard S-curve card.
 * Route: /dashboard/overview
 * Monthly planned vs actual chart with a metric switch.
 * Does not own the report table or the granularity control.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ActionIcon,
  Button,
  Card,
  Group,
  HoverCard,
  SegmentedControl,
  Skeleton,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';
import {
  fetchSCurve,
  S_CURVE_METRICS,
  type SCurveData,
  type SCurveMetric,
} from '../../../api/sCurveApi';
import { usePortalProject } from '../../../contexts/PortalProjectContext';
import { APP_ROUTES } from '../../../navigation/appRoutes';
import SCurveChart from '../../reports-s-curve/components/SCurveChart';
import { mapSCurveChart } from '../../reports-s-curve/logic/mapSCurveChart';

const CARD_RADIUS = 'sm';
const CHART_HEIGHT = 300;

const METRIC_OPTIONS: { value: SCurveMetric; label: string }[] = [
  { value: 'QUANTITY', label: 'Quantity' },
  { value: 'SCHEDULE_PERCENT', label: 'Schedule %' },
];

const INFO_PARAGRAPHS = [
  'The graph shows how much of the project is done over time, compared with how much the plan said should be done.',
  'The horizontal axis is time. You\u2019re seeing Monthly view. The vertical axis is cumulative progress, from 0% to 100%. Each point is a running total, so the lines climb as more work is counted.',
  'Planned (blue) is the plan\u2019s running total. It keeps going through the end of the project.',
  'Actual (teal) is the running total of work recorded so far. It stops at today. Periods after today are left blank, so the line does not continue and does not drop to zero.',
  'Where both lines exist, the gap is the variance: actual minus planned. The teal line above the blue line means the work is ahead of plan. The teal line below means it is behind.',
  'Quantity uses planned and achieved quantities. Schedule % uses the schedule itself with latest baseline information.',
];

const controlStyles = {
  root: {
    border: '1px solid var(--mantine-color-default-border)',
  },
} as const;

const viewReportStyles = {
  root: {
    backgroundColor: 'var(--mantine-color-body)',
    color: 'var(--mantine-color-text)',
    border: '1px solid var(--mantine-color-default-border)',
    fontWeight: 500,
    '&:hover': {
      backgroundColor: 'var(--mantine-color-default-hover)',
    },
  },
} as const;

export function DashboardSCurveCard() {
  const { projectId } = usePortalProject();
  const [metric, setMetric] = useState<SCurveMetric>('QUANTITY');
  const [data, setData] = useState<SCurveData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!projectId) {
      setData(null);
      setError(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setData(null);
    setLoading(true);
    setError(null);
    fetchSCurve({ projectId, metric, granularity: 'MONTH' })
      .then((next) => {
        if (!cancelled) setData(next);
      })
      .catch(() => {
        if (cancelled) return;
        setData(null);
        setError('Could not load the S-curve for this project.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId, metric]);

  const model = useMemo(() => (data ? mapSCurveChart(data) : null), [data]);

  return (
    <Card withBorder radius={CARD_RADIUS} p="md">
      <Group justify="space-between" align="center" mb="sm" wrap="wrap" gap="sm">
        <Group gap={4} wrap="nowrap" style={{ minWidth: 0 }}>
          <Title order={4}>S-curve</Title>
          <HoverCard width={440} shadow="md" withArrow position="bottom-start" openDelay={150} closeDelay={80}>
            <HoverCard.Target>
              <ActionIcon variant="subtle" color="gray" size="sm" aria-label="About S-curve">
                <IconInfoCircle size={16} stroke={1.5} />
              </ActionIcon>
            </HoverCard.Target>
            <HoverCard.Dropdown>
              <Stack gap="xs">
                {INFO_PARAGRAPHS.map((paragraph) => (
                  <Text key={paragraph} size="sm">
                    {paragraph}
                  </Text>
                ))}
              </Stack>
            </HoverCard.Dropdown>
          </HoverCard>
        </Group>
        <Group gap="sm" wrap="wrap" style={{ flexShrink: 0 }}>
          <SegmentedControl
            size="xs"
            value={metric}
            onChange={(value) => {
              if ((S_CURVE_METRICS as readonly string[]).includes(value)) {
                setMetric(value as SCurveMetric);
              }
            }}
            data={METRIC_OPTIONS}
            styles={controlStyles}
          />
          <Button
            component={Link}
            to={APP_ROUTES.reports.sCurve}
            size="xs"
            variant="default"
            styles={viewReportStyles}
          >
            View report
          </Button>
        </Group>
      </Group>

      {!projectId ? <Text size="sm" c="dimmed">Select a project to load the S-curve.</Text> : null}
      {error ? (
        <Text size="sm" c="red">
          {error}
        </Text>
      ) : null}
      {loading && !model ? <Skeleton h={CHART_HEIGHT} radius="sm" /> : null}
      {model && model.rows.length === 0 ? (
        <Text size="sm" c="dimmed">
          No S-curve points for this project and metric.
        </Text>
      ) : null}
      {model && model.rows.length > 0 ? (
        <SCurveChart rows={model.rows} showPeriodAmounts={model.showPeriodAmounts} h={CHART_HEIGHT} />
      ) : null}
    </Card>
  );
}
